import { Link } from 'react-router-dom';
import { ArrowRight, Stethoscope, Users, Check, Calendar, Heart, GraduationCap, Brain, Sparkles, FileText, Shield } from '../components/Icons.jsx';
import CohortWidget from '../components/CohortWidget.jsx';
import BursaryCheck from '../components/BursaryCheck.jsx';

const cities = [
  {
    name: 'London',
    tagline: 'Capital teaching hospitals + research institutes',
    bullets: [
      'Hospital + hospice rotation',
      'A&E, surgical, GP and mental health observations',
      'Workshops led by active NHS clinicians',
      'Visits to leading research institutions'
    ]
  },
  {
    name: 'Birmingham',
    tagline: 'GP-shadowing cohort — strong for Midlands applicants',
    bullets: [
      'Live GP-surgery placement with patient contact',
      'Clinician-led communication-skills workshops',
      'Case-based discussions on primary-care medicine',
      'Strong route for Year 12 students outside the south-east'
    ]
  },
  {
    name: 'Manchester',
    tagline: 'GP-shadowing cohort — best route for the north-west',
    bullets: [
      'Live GP-surgery placement with patient contact',
      'Communication-skills workshops led by NHS GPs',
      'Primary-care reflections mapped to GMC outcomes',
      'Same Career Camp reflection workbook as London'
    ]
  }
];

const week = [
  { day: 'Mon', title: 'Welcome + hospital induction', body: 'Cohort briefing, GMC professionalism induction, ward orientation. First patient observations same day.' },
  { day: 'Tue', title: 'Surgical specialties', body: 'Theatre observation (where consent permits), pre-op assessment, surgical decision-making case study.' },
  { day: 'Wed', title: 'Emergency + acute medicine', body: 'A&E shadowing, triage observation, acute medical unit. Communication-skills workshop in the afternoon.' },
  { day: 'Thu', title: 'Hospice + palliative care', body: 'Hospice placement — observation, conversations with palliative-care leads. The day that most candidates say redefined their motivation.' },
  { day: 'Fri', title: 'GP + reflections + close', body: 'GP-surgery morning. Afternoon: structured reflection workshop, GMC-outcomes mapping, certificate ceremony.' }
];

const outcomes = [
  { icon: <Check />, title: 'Six GMC-mapped reflections', body: 'Written during the week using the Career Camp reflection workbook. Each maps to one of the four GMC outcomes.' },
  { icon: <FileText />, title: 'Personal-statement templates', body: 'Drop-in paragraphs from your reflections — the structure works on UCAS character limits.' },
  { icon: <Users />, title: 'Two interview-ready stories', body: 'Specific moments from the week you can deploy in MMI ethics stations and motivation questions.' },
  { icon: <Shield />, title: 'Signed certificate of attendance', body: 'For your application and your school records. Signed by the Medic Mind clinical lead.' }
];

const faqs = [
  { q: 'Who runs Career Camp?', a: 'Medic Mind. Sessions are led by active NHS doctors, surgeons and medical researchers — the same network that runs Medic Mind\'s UCAT tutoring and 1-to-1 admissions support.' },
  { q: 'Who can apply?', a: 'Students aged 15–18 actively considering a medical or dental career. Year 11s through Year 13s are all welcome; the strongest single window is the Year 12 summer.' },
  { q: 'How much does it cost? Are bursaries available?', a: 'Standard pricing varies by city and cohort — ask in your free 15-min advisor call for current rates. Bursaries cover 50%, 75% or 100% for widening-participation eligible applicants (free-school-meals, first-in-family, bursary-funded school, low-income household).' },
  { q: 'How is this different from virtual work experience?', a: 'Virtual work experience can\'t replicate the patient contact, ward rhythm or clinician conversations that admissions tutors actually score. Career Camp puts you inside a working NHS hospital and a hospice, then maps everything you see to your application. We also offer a free virtual option as a top-up.' },
  { q: 'Do I need previous experience?', a: 'No. Many Career Camp students come in with no formal placements. The week is structured for first-timers.' },
  { q: 'What about insurance and safety?', a: 'Cohorts are capped at 12 students. All placements are fully insured by Medic Mind. Safeguarding and confidentiality briefings happen at induction.' }
];

export default function CareerCamp() {
  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Medicine Summer Career Camp</div>
        <h1>Five days inside a working NHS hospital — built for your application.</h1>
        <p className="subtitle">Real clinical placements led by Medic Mind doctors, surgeons and medical researchers. Full hospital + hospice rotations in London; GP shadowing cohorts in Birmingham and Manchester. For students aged 15–18, with bursaries up to 100% for widening-participation applicants.</p>
      </div>

      <section className="card" style={{
        background: 'linear-gradient(135deg, var(--feature-from), var(--feature-via), var(--feature-to))',
        color: '#fff', border: 'none', marginBottom: 24, padding: '28px 32px'
      }}>
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.15)', borderRadius: 14, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Stethoscope width="28" height="28" />
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em' }}>What you'll walk away with.</h2>
            <p style={{ margin: 0, color: '#e9d5ff', maxWidth: 580 }}>Application-ready reflections, interview stories and a certificate — not a recorded video. The week is designed to convert directly into your personal statement and your MMI answers.</p>
          </div>
          <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer" style={{ background: '#fff', color: 'var(--brand-deep)' }}>Book free advisor call <ArrowRight width="16" height="16" /></a>
        </div>
      </section>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Three cities. One programme.</h2>
      <div className="parents-grid" style={{ marginBottom: 28 }}>
        {cities.map((c) => (
          <div key={c.name} className="parents-card">
            <div className="icon-wrap"><Stethoscope /></div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>5 days · in-person</div>
            <h3>{c.name}</h3>
            <p style={{ color: 'var(--text-muted)', margin: '4px 0 10px', fontSize: 13.5 }}>{c.tagline}</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--text-2)', fontSize: 14 }}>
              {c.bullets.map((b) => (
                <li key={b} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--accent)' }}>▸</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>A typical week</h2>
      <div className="card" style={{ padding: 0, marginBottom: 28, overflow: 'hidden' }}>
        {week.map((d, i) => (
          <div key={d.day} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 0, borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
            <div style={{ background: 'var(--card-soft)', padding: '18px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
              <strong style={{ fontSize: 14, color: 'var(--brand-purple)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{d.day}</strong>
            </div>
            <div style={{ padding: '18px 20px' }}>
              <h4 style={{ margin: '0 0 4px', fontSize: 15.5 }}>{d.title}</h4>
              <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 14, lineHeight: 1.5 }}>{d.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Application outcomes</h2>
      <div className="r-grid-2" style={{ marginBottom: 28 }}>
        {outcomes.map((o) => (
          <div key={o.title} className="card" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--accent-soft)', color: 'var(--brand-purple)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>{o.icon}</div>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: 15.5 }}>{o.title}</h4>
              <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 14, lineHeight: 1.5 }}>{o.body}</p>
            </div>
          </div>
        ))}
      </div>

      <section className="card" style={{ marginBottom: 28, borderLeft: '5px solid var(--brand-teal)' }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 22 }}>Bursaries — 50%, 75% or 100%</h2>
        <p style={{ margin: 0, color: 'var(--text-2)' }}>If you're widening-participation eligible — free-school-meals, first-in-family, bursary-funded school, or a low-income household — Medic Mind covers half, three-quarters or the full cost of Career Camp. This is the lever that closes the gap with privately-tutored applicants. Ask in your free 15-min advisor call.</p>
      </section>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Common questions</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {faqs.map((f) => (
          <details key={f.q} className="card" style={{ padding: 0 }}>
            <summary style={{ padding: '16px 20px', cursor: 'pointer', fontSize: 15, fontWeight: 600, listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {f.q}
              <span style={{ fontSize: 18, color: 'var(--text-muted)', flexShrink: 0, marginLeft: 12 }}>+</span>
            </summary>
            <div style={{ padding: '0 20px 18px', color: 'var(--text-2)', fontSize: 14, lineHeight: 1.6 }}>{f.a}</div>
          </details>
        ))}
      </div>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Use these tools after the week</h2>
      <div className="r-grid-3" style={{ marginBottom: 28 }}>
        <Link to="/we-reflection" className="parents-card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="icon-wrap"><FileText /></div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>Writer</div>
          <h3>Reflection generator</h3>
          <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '8px 0 0' }}>Turn each day's notes into a PS-ready paragraph mapped to a GMC outcome.</p>
        </Link>
        <Link to="/we-quiz" className="parents-card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="icon-wrap"><Brain /></div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>Diagnostic</div>
          <h3>Work experience strength quiz</h3>
          <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '8px 0 0' }}>Score your full work-experience profile on four axes and see what to add next.</p>
        </Link>
        <Link to="/we-finder" className="parents-card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="icon-wrap"><Stethoscope /></div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>Tracker</div>
          <h3>WE finder &amp; log</h3>
          <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '8px 0 0' }}>Record placements and hours; surface longitudinal add-ons to pair with Career Camp.</p>
        </Link>
      </div>

      <CohortWidget />

      <BursaryCheck />

      <section className="parents-cta" style={{ marginTop: 0 }}>
        <div>
          <h2>See available Career Camp cohorts.</h2>
          <p>London runs the full hospital + hospice week year-round. Birmingham and Manchester run GP-shadowing cohorts. Each cohort caps at 12 — early enquiries get first pick of dates and bursary places.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book free advisor call <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}
